import { useSelector } from "react-redux";
import DataTableBase from "./DataTableBase";
import { useTranslation } from "react-i18next";
import React from "react";
import { useNavigate } from "react-router-dom";
import { store } from "../../store";
import { Box, Typography } from "@mui/material";
import {
  WaterDrop,
  ElectricBolt,
  Tv,
  DevicesOther,
  Router,
  AccessTime,
  Thermostat,
  Home,
  TouchApp,
  ToggleOn,
  Battery20,
  Battery50,
  Battery80,
  BatteryFull,
  SensorDoor,
} from "@mui/icons-material";

// Icône selon le type de device
const getDeviceIcon = (device) => {
  const typeName = device?.device_brand?.device_type?.name?.toLowerCase() ||
                   device?.device_type?.name?.toLowerCase() || "";

  if (typeName.includes("water") || typeName.includes("eau")) {
    return <WaterDrop sx={{ fontSize: 22, color: "#6b9dad" }} />;
  }
  if (typeName.includes("elec") || typeName.includes("power") || typeName.includes("énergie")) {
    return <ElectricBolt sx={{ fontSize: 22, color: "#c4a574" }} />;
  }
  if (typeName.includes("screen") || typeName.includes("écran") || typeName.includes("display")) {
    return <Tv sx={{ fontSize: 22, color: "#8a9ba8" }} />;
  }
  if (typeName.includes("temperature") || typeName.includes("temp") || typeName.includes("thermo")) {
    return <Thermostat sx={{ fontSize: 22, color: "#e07a5f" }} />;
  }
  if (typeName.includes("switch") || typeName.includes("relay")) {
    return <ToggleOn sx={{ fontSize: 22, color: "#9c88b8" }} />;
  }
  if (typeName.includes("button") || typeName.includes("bouton")) {
    return <TouchApp sx={{ fontSize: 22, color: "#d4a574" }} />;
  }
  if (typeName.includes("door") || typeName.includes("porte") || typeName.includes("contact")) {
    return <SensorDoor sx={{ fontSize: 22, color: "#7eb8c4" }} />;
  }
  return <DevicesOther sx={{ fontSize: 22, color: "#7a8a7a" }} />;
};

// Icône batterie selon le pourcentage
const getBatteryIcon = (battery) => {
  if (battery === null || battery === undefined) return null;
  if (battery <= 20) return <Battery20 sx={{ fontSize: 18, color: "#a57474" }} />;
  if (battery <= 50) return <Battery50 sx={{ fontSize: 18, color: "#c4a574" }} />;
  if (battery <= 80) return <Battery80 sx={{ fontSize: 18, color: "#78a882" }} />;
  return <BatteryFull sx={{ fontSize: 18, color: "#78a882" }} />;
};

// Label pour button_status
const getButtonStatusLabel = (status) => {
  const statusMap = {
    short_press: { label: "Appui court", color: "#78a882" },
    long_press: { label: "Appui long", color: "#c4a574" },
    double_press: { label: "Double appui", color: "#6b9dad" },
  };
  return statusMap[status] || { label: status, color: "#9aa89a" };
};

export default function DevicesTable({ filteredDevices }) {
  const allDevices = useSelector((state) => state.devices);
  const packs = useSelector((state) => state.packs);
  const residences = useSelector((state) => state.residences);

  // Utiliser les devices filtrés si fournis, sinon tous les devices
  const devices = filteredDevices || allDevices;

  const navigate = useNavigate();

  // Fonction pour obtenir le nom de l'installation (résidence) d'un device
  const getDeviceResidenceName = (device) => {
    // Trouver le pack qui contient ce device
    const pack = packs.find((p) =>
      p.devices?.some((d) => d.dev_eui === device.dev_eui)
    );
    if (!pack) return null;

    // Trouver la résidence associée au pack
    const residence = residences.find((r) => r.pack_id === pack.id);
    return residence?.name || null;
  };

  const [selectedDevices, setSelecteddevices] = React.useState([]);
  const [selectedRows, setSelectedRows] = React.useState([]);

  const { t } = useTranslation("common");

  const columns = [
    {
      name: "dev_eui",
      label: "Dev EUI",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Router sx={{ color: "#78a882", fontSize: 18 }} />
            <Typography
              variant="body2"
              sx={{ fontFamily: "monospace", fontWeight: 500 }}
            >
              {value}
            </Typography>
          </Box>
        ),
      },
    },
    {
      name: "name",
      label: t("common.name"),
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {value || "—"}
          </Typography>
        ),
      },
    },
    {
      name: "device_brand",
      label: "Type",
      options: {
        filter: true,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const device = devices[dataIndex];
          return (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              {getDeviceIcon(device)}
            </Box>
          );
        },
        sortCompare: (order) => {
          return (obj1, obj2) => {
            const getTypeName = (device) =>
              device?.device_brand?.device_type?.name?.toLowerCase() ||
              device?.device_type?.name?.toLowerCase() || "";
            const val1 = getTypeName(obj1.data);
            const val2 = getTypeName(obj2.data);
            return val1.localeCompare(val2) * (order === "asc" ? 1 : -1);
          };
        },
      },
    },
    {
      name: "status",
      label: "État",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const device = devices[dataIndex];
          const typeName = device?.device_brand?.device_type?.name?.toLowerCase() ||
                           device?.device_type?.name?.toLowerCase() || "";

          // Affichage pour les boutons
          if (typeName.includes("button") || typeName.includes("bouton")) {
            const buttonStatus = device?.last_data?.button_status || device?.button_status;
            const battery = device?.last_data?.battery || device?.battery;
            const statusInfo = getButtonStatusLabel(buttonStatus);

            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {buttonStatus ? (
                  <Typography
                    variant="body2"
                    sx={{
                      color: statusInfo.color,
                      fontWeight: 500,
                      fontSize: "0.8rem",
                    }}
                  >
                    {statusInfo.label}
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ color: "#9aa89a" }}>—</Typography>
                )}
                {getBatteryIcon(battery)}
              </Box>
            );
          }

          // Affichage pour les relays/switches
          if (typeName.includes("switch") || typeName.includes("relay")) {
            const relayState = device?.last_data?.state || device?.state;
            const isOn = relayState === true || relayState === "on" || relayState === 1;
            const isOff = relayState === false || relayState === "off" || relayState === 0;

            if (isOn || isOff) {
              return (
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    backgroundColor: isOn ? "rgba(120, 168, 130, 0.15)" : "rgba(165, 116, 116, 0.15)",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: isOn ? "#78a882" : "#a57474",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                    }}
                  >
                    {isOn ? "ON" : "OFF"}
                  </Typography>
                </Box>
              );
            }
          }

          return <Typography variant="body2" sx={{ color: "#9aa89a" }}>—</Typography>;
        },
      },
    },
    {
      name: "installation",
      label: t("common.residence"),
      options: {
        filter: true,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const device = devices[dataIndex];
          const residenceName = getDeviceResidenceName(device);
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Home sx={{ fontSize: 18, color: residenceName ? "#78a882" : "#c4a574" }} />
              <Typography
                variant="body2"
                sx={{
                  color: residenceName ? "text.primary" : "#9aa89a",
                  fontStyle: residenceName ? "normal" : "italic",
                }}
              >
                {residenceName || t("common.not_assigned")}
              </Typography>
            </Box>
          );
        },
        sortCompare: (order) => {
          return (obj1, obj2) => {
            const getName = (idx) => {
              const device = devices[idx];
              return getDeviceResidenceName(device) || "";
            };
            const val1 = getName(obj1.rowIndex);
            const val2 = getName(obj2.rowIndex);
            return val1.localeCompare(val2) * (order === "asc" ? 1 : -1);
          };
        },
      },
    },
    {
      name: "fcnt",
      label: t("common.fcnt"),
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => (
          <Typography
            variant="body2"
            sx={{ color: "#9aa89a", fontFamily: "monospace" }}
          >
            {value || 0}
          </Typography>
        ),
      },
    },
    {
      name: "time_updated",
      label: t("common.last_seen"),
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => {
          if (!value) return "—";
          const date = new Date(value);
          const now = new Date();
          const diffMs = now - date;
          const diffHours = diffMs / (1000 * 60 * 60);

          // Couleur selon la fraîcheur des données
          let color = "#78a882"; // Vert si < 1h
          if (diffHours > 24) color = "#c4a574"; // Orange si > 24h
          if (diffHours > 72) color = "#a57474"; // Rouge si > 72h

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <AccessTime sx={{ fontSize: 16, color: color }} />
              <Typography variant="body2" sx={{ color: color }}>
                {date.toLocaleString()}
              </Typography>
            </Box>
          );
        },
      },
    },
  ];

  const tableActions = [
    {
      name: t("common.delete_selected", {
        name: t("common.devices"),
        count: selectedDevices.length,
      }),
      icon: "Delete",
      action: () => {
        store.dispatch({
          type: "deleteDialog/update",
          payload: {
            open: true,
            type: "devices",
            records: selectedDevices,
          },
        });
      },
    },
  ];

  return (
    <DataTableBase
      columns={columns}
      data={devices}
      title={t("common.devices")}
      id="device-list"
      name="device-list"
      pageSize={10}
      selectedItems={selectedDevices}
      setSelectedItems={setSelecteddevices}
      selectedRows={selectedRows}
      setSelectedRows={setSelectedRows}
      selectableRows="multiple"
      actions={tableActions}
      sortOrder="name"
      onRowClick={(rowData, rowMeta) => {
        // Utiliser rowMeta.dataIndex pour accéder aux données originales
        const device = devices[rowMeta.dataIndex];
        if (device && device.dev_eui) {
          navigate(`/devices/${device.dev_eui}`);
        }
      }}
      useDevEui
    />
  );
}
